// Define your items here, similar to recurringEventEndTypeRadioItems


// Forms Checkbox Items


// risk assessment checkbox items

// risk assessment single-select risk level options
export type RiskLevelOption = {
  readonly value: string;
  readonly label: string;
  readonly description: string;
};

export const RISK_LEVEL_OPTIONS: readonly RiskLevelOption[] = [
  {
    value: "low_risk",
    label: "Low Risk",
    description:
      "Patient denies suicidal ideation, homicidal ideation, self-injurious behaviors, and psychotic symptoms. No history of suicide attempts. Demonstrates good insight, future orientation, and strong family support. Acute risk assessed as low.",
  },
  {
    value: "passive_suicidal_ideation",
    label: "Passive Suicidal Ideation",
    description:
      "Patient endorses passive thoughts of death but denies suicidal intent, plan, or preparatory behaviors. Protective factors include family support, employment, and willingness to seek help. Acute suicide risk assessed as low to moderate.",
  },
  {
    value: "moderate_suicide_risk",
    label: "Moderate Suicide Risk",
    description:
      "Patient reports intermittent suicidal ideation but denies current intent. History of prior suicide attempt. Risk factors include depression and social isolation. Protective factors include engagement in treatment. Acute risk assessed as moderate.",
  },
  {
    value: "high_suicide_risk",
    label: "High Suicide Risk",
    description:
      "Patient endorses active suicidal ideation with specific plan and intent. Access to means. Recent suicide attempt. Acute suicide risk assessed as high; higher level of care recommended.",
  },
  {
    value: "self_harm_risk",
    label: "Self-Harm Risk",
    description:
      "Patient reports recent NSSI during periods of emotional distress. Denies suicidal intent. Risk for recurrent self-harm remains elevated due to poor coping skills and emotional dysregulation.",
  },
  {
    value: "homicide_violence_risk_low",
    label: "Homicide/Violence Risk – Low",
    description:
      "Patient denies homicidal ideation, violent thoughts, or history of aggression. No evidence of imminent risk to others.",
  },
  {
    value: "homicide_violence_risk_moderate",
    label: "Homicide/Violence Risk – Moderate",
    description:
      "Patient reports anger and occasional thoughts of confrontation but denies plan, intent, or access to weapons. Violence risk assessed as low to moderate.",
  },
  {
    value: "psychosis_related_risk",
    label: "Psychosis-Related Risk",
    description:
      "Patient experiences command auditory hallucinations. Insight is limited. Given psychotic symptoms and impaired judgment, risk is elevated and requires close monitoring.",
  },
  {
    value: "substance_use_risk",
    label: "Substance Use Risk",
    description:
      "Ongoing substance use contributing to impaired judgment and increased impulsivity. Denies suicidal ideation. Substance use elevates overall risk despite absence of current suicidal intent.",
  },
] as const;































// Physical Assessment exam sections (comma-separated string payload)
// e.g. { appearance: "cleanGroomed,tattoos" }
export type ExamCheckboxSection = {
  key: string;
  label: string;
  items: { name: string; label: string }[];
  // Override for the free-text note field key. Defaults to `${key}Note`.
  noteKey?: string;
};

export const PHYSICAL_ASSESSMENT_EXAM_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "appearance",
    label: "Appearance",
    items: [
      { name: "cleanGroomed", label: "Clean/groomed" },
      { name: "disheveled", label: "Disheveled" },
      {
        name: "dressedAppropriatelyForClimate",
        label: "Dressed appropriately for climate",
      },
      { name: "piercings", label: "Piercings" },
      { name: "prominentScars", label: "Prominent scars" },
      { name: "tattoos", label: "Tattoos" },
      { name: "wearingMakeup", label: "Wearing makeup" },
      { name: "appearsStatedAge", label: "Appears stated age" },
      { name: "appearsOlderThanStatedAge", label: "Appears older than stated age" },
      {
        name: "appearsYoungerThanStatedAge",
        label: "Appears younger than stated age",
      },
      { name: "wellNourished", label: "Well nourished" },
      { name: "normalBMI", label: "Normal BMI" },
      { name: "obese", label: "Obese" },
      { name: "wellHydrated", label: "Well hydrated" },
    ],
  },
  {
    key: "behavior",
    label: "Behavior / Psychomotor Activity",
    items: [
      { name: "cooperative", label: "Cooperative" },
      { name: "calmRelaxed", label: "Calm/relaxed" },
      { name: "agitation", label: "Agitation" },
      { name: "combative", label: "Combative" },
      { name: "compulsiveBehaviors", label: "Compulsive behaviors" },
      { name: "eyeContactDecreased", label: "Eye contact decreased" },
      { name: "eyeContactNormal", label: "Eye contact normal" },
      { name: "fidgety", label: "Fidgety" },
      { name: "hypervigilant", label: "Hypervigilant" },
      { name: "overlySolicitous", label: "Overly solicitous" },
      { name: "posturing", label: "Posturing" },
      { name: "psychomotorRetardation", label: "Psychomotor retardation" },
      { name: "rigid", label: "Rigid" },
      { name: "twitching", label: "Twitching" },
    ],
  },
  {
    key: "speech",
    label: "Speech",
    items: [
      { name: "speakingNormalRateAndVolume", label: "Normal rate and volume" },
      { name: "pressuredButInterruptible", label: "Pressured but interruptible" },
      { name: "loud", label: "Loud" },
      { name: "softSpeakingWhispering", label: "Soft/whispering" },
      { name: "monotoned", label: "Monotone" },
      { name: "stuttering", label: "Stuttering" },
      { name: "speakingFastRate", label: "Speaking – fast rate" },
      { name: "speakingSlowRate", label: "Speaking – slow rate" },
      { name: "speakingNone", label: "Speaking – none" },
      {
        name: "pausesBetweenQuestionsAndAnswerPoverty",
        label: "Pauses between answers (poverty of speech)",
      },
    ],
  },
  {
    key: "cooperativeness",
    label: "Cooperativeness",
    items: [
      { name: "cooperative", label: "Cooperative" },
      { name: "defensive", label: "Defensive" },
      { name: "evasive", label: "Evasive" },
      { name: "friendly", label: "Friendly" },
      { name: "hostile", label: "Hostile" },
      { name: "ingratiating", label: "Ingratiating" },
      { name: "reluctant", label: "Reluctant" },
      { name: "seductive", label: "Seductive" },
    ],
  },
  {
    key: "mood",
    label: "Mood / Affect",
    noteKey: "moodAffectNote",
    items: [
      { name: "euthymic", label: "Euthymic" },
      { name: "depressed", label: "Depressed" },
      { name: "anxious", label: "Anxious" },
      { name: "irritable", label: "Irritable" },
      { name: "angry", label: "Angry" },
      { name: "euphoric", label: "Euphoric" },
      { name: "elated", label: "Elated" },
      { name: "happy", label: "Happy" },
      { name: "labile", label: "Labile" },
      { name: "flat", label: "Flat" },
      { name: "blunted", label: "Blunted" },
      { name: "restricted", label: "Restricted" },
      { name: "fullRange", label: "Full range" },
      { name: "constricted", label: "Constricted" },
      { name: "inappropriate", label: "Inappropriate" },
      {
        name: "suicidalHomicidalIdeationNoted",
        label: "Suicidal/Homicidal ideation noted",
      },
    ],
  },
  {
    key: "thoughtProcess",
    label: "Thought Process",
    items: [
      { name: "linearLogicalGoalDirected", label: "Linear, logical, goal-directed" },
      { name: "circumstantial", label: "Circumstantial" },
      { name: "tangential", label: "Tangential" },
      { name: "flightOfIdeas", label: "Flight of ideas" },
      { name: "looseAssociations", label: "Loose associations" },
      { name: "thoughtBlocking", label: "Thought blocking" },
      { name: "thoughtDerailment", label: "Thought derailment" },
      { name: "perseverative", label: "Perseverative" },
      { name: "normalAssociations", label: "Normal associations" },
    ],
  },
  {
    key: "intellectualFunctioning",
    label: "Intellectual Functioning & Insight",
    items: [
      {
        name: "languageMatchesLevelOfEducation",
        label: "Language matches level of education",
      },
      { name: "noInsight", label: "No insight" },
      {
        name: "understandsAndBelievesTheyHaveAnIssue",
        label: "Understands and believes they have an issue",
      },
    ],
  },
  {
    key: "thoughtContent",
    label: "Thought Content",
    items: [
      { name: "noDelusionsOrObsessions", label: "No delusions or obsessions" },
      { name: "delusionsPresent", label: "Delusions present" },
      { name: "preoccupationsObsessions", label: "Preoccupations/obsessions" },
      {
        name: "plansAndGoalsForTheFuture",
        label: "Plans and goals for the future",
      },
      {
        name: "fearsConcernsRegardingDanger",
        label: "Fears/concerns regarding danger",
      },
      { name: "ritualRuminations", label: "Ritual ruminations" },
      { name: "phobias", label: "Phobias" },
      { name: "worryingAboutThePast", label: "Worrying about the past" },
    ],
  },
  {
    key: "perceptions",
    label: "Perception",
    noteKey: "perceptionNote",
    items: [
      { name: "noHallucinationsReported", label: "No hallucinations reported" },
      { name: "auditoryHallucinations", label: "Auditory hallucinations" },
      { name: "visualHallucinations", label: "Visual hallucinations" },
      { name: "tactileHallucinations", label: "Tactile hallucinations" },
      { name: "commandHallucinations", label: "Command hallucinations" },
      { name: "paranoidIdeation", label: "Paranoid ideation" },
    ],
  },
  {
    key: "cognition",
    label: "Cognition",
    items: [
      { name: "alertAndOrientedX4", label: "Alert and oriented ×4" },
      { name: "alertAndOrientedX3", label: "Alert and oriented ×3" },
      { name: "alertAndOrientedX2", label: "Alert and oriented ×2" },
      {
        name: "attentionConcentrationIntact",
        label: "Attention/concentration intact",
      },
      {
        name: "attentionConcentrationImpaired",
        label: "Attention/concentration impaired",
      },
      { name: "memoryIntact", label: "Memory intact" },
      { name: "memoryImpaired", label: "Memory impaired" },
    ],
  },
  {
    key: "insight",
    label: "Insight",
    items: [
      { name: "good", label: "Good" },
      { name: "fair", label: "Fair" },
      { name: "poor", label: "Poor" },
    ],
  },
  {
    key: "judgment",
    label: "Judgment",
    items: [
      { name: "intact", label: "Intact" },
      { name: "mildlyImpaired", label: "Mildly impaired" },
      { name: "moderatelyImpaired", label: "Moderately impaired" },
      { name: "severelyImpaired", label: "Severely impaired" },
    ],
  },
];
